import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import { formatDate, todayLocalISO } from '../../utils/dateUtils';
import FileUploadWidget from '../../components/FileUploadWidget';

const Certifications = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const { activeSiteId, activeSiteName } = useSite();
  const { hasPermission } = usePermissions();
  const [certifications, setCertifications] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [initialAttachmentIds, setInitialAttachmentIds] = useState([]);

  // Attachment viewer state (read-only modal)
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [viewingAttachments, setViewingAttachments] = useState([]);
  const [viewingAttachmentLoading, setViewingAttachmentLoading] = useState(false);

  const [certForm, setCertForm] = useState({
    employee_id: '',
    employee_name: '',
    certificate_type: 'Safety Certificate',
    certificate_name: '',
    issuing_body: '',
    certificate_number: '',
    // CMP-21: the user's calendar date, not the UTC date (which is
    // "yesterday" in IST between 00:00 and 05:30). Not todayTenantISO():
    // tenants without settings.timezone report "UTC" and would keep the bug.
    issue_date: todayLocalISO(),
    expiry_date: '',
    validity_period_months: '',
    auto_renewal: false
  });

  useEffect(() => {
    fetchData();
    // Check if coming from expiring filter
    const params = new URLSearchParams(location.search);
    if (params.get('filter') === 'expiring') {
      setFilterStatus('Expiring Soon');
    }
  }, [location, activeSiteId]);

  // Auto-open edit modal when navigated with ?edit=ID from detail page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (editId && certifications.length > 0) {
      const certToEdit = certifications.find(c => c.id === editId);
      if (certToEdit) {
        openModal(certToEdit);
        navigate('/training/certifications', { replace: true });
      }
    }
  }, [certifications, location.search]);

  useEffect(() => {
    if (filterStatus === 'All') {
      setFiltered(certifications);
    } else {
      setFiltered(certifications.filter(c => c.status === filterStatus));
    }
  }, [filterStatus, certifications]);

  useEffect(() => {
    if (employeeSearch.trim() === '') {
      setFilteredEmployees(employees);
    } else {
      const search = employeeSearch.toLowerCase();
      setFilteredEmployees(
        employees.filter(emp => 
          (emp.name && emp.name.toLowerCase().includes(search)) ||
          (emp.email && emp.email.toLowerCase().includes(search)) ||
          (emp.id && emp.id.toLowerCase().includes(search))
        )
      );
    }
  }, [employeeSearch, employees]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
    };

    if (showEmployeeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmployeeDropdown]);

  // Auto-calculate expiry date from validity period
  useEffect(() => {
    if (certForm.issue_date && certForm.validity_period_months) {
      const validityMonths = parseInt(certForm.validity_period_months);
      const [y, m, d] = String(certForm.issue_date).split('-').map(Number);
      if (!isNaN(validityMonths) && validityMonths > 0 && y && m && d) {
        // CMP-21: pure calendar arithmetic in UTC so the result never depends
        // on the browser's timezone or the time of day.
        const expiryDate = new Date(Date.UTC(y, m - 1 + validityMonths, d));
        setCertForm(prev => ({
          ...prev,
          expiry_date: expiryDate.toISOString().split('T')[0]
        }));
      }
    }
  }, [certForm.issue_date, certForm.validity_period_months]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // The users list only feeds the "assign certification" modal. Roles that
      // can view their own certs but not list users (e.g. contractor → users:read
      // 403) must still see their certifications, so this fetch must not reject
      // the whole batch — degrade it to an empty list on failure.
      const [certsRes, usersRes] = await Promise.all([
        axios.get(`${API}/certifications`, { headers }),
        axios.get(activeSiteId ? `${API}/users?site_id=${activeSiteId}` : `${API}/users`, { headers })
          .catch(() => ({ data: [] }))
      ]);

      setCertifications(certsRes.data);
      setFiltered(certsRes.data);
      setEmployees(usersRes.data);
      setFilteredEmployees(usersRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (cert = null) => {
    // QA #63/#97: new certifications are site-scoped — require a site BEFORE
    // the form opens, so users never fill it in only to fail on submit.
    if (!cert && !activeSiteId) {
      showWarning('Please select a site from the site selector before adding a certification.');
      return;
    }
    if (cert) {
      setEditingCert(cert);
      setCertForm({
        employee_id: cert.employee_id,
        employee_name: cert.employee_name,
        certificate_type: cert.certificate_type,
        certificate_name: cert.certificate_name,
        issuing_body: cert.issuing_body,
        certificate_number: cert.certificate_number,
        issue_date: cert.issue_date.split('T')[0],
        expiry_date: cert.expiry_date ? cert.expiry_date.split('T')[0] : '',
        validity_period_months: cert.validity_period_months || '',
        auto_renewal: cert.auto_renewal || false
      });
      setEmployeeSearch(cert.employee_name);

      // Fetch existing attachments for this certification
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/certifications/${cert.id}/attachments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const files = res.data.attachments || [];
        setUploadedFiles(files);
        setInitialAttachmentIds(files.map(f => f.id));
      } catch (err) {
        console.error('Failed to fetch attachments:', err);
        setUploadedFiles([]);
        setInitialAttachmentIds([]);
      }
    } else {
      setEditingCert(null);
      setCertForm({
        employee_id: '',
        employee_name: '',
        certificate_type: 'Safety Certificate',
        certificate_name: '',
        issuing_body: '',
        certificate_number: '',
        issue_date: todayLocalISO(),
        expiry_date: '',
        validity_period_months: '',
        auto_renewal: false
      });
      setEmployeeSearch('');
      setUploadedFiles([]);
      setInitialAttachmentIds([]);
    }
    setShowEmployeeDropdown(false);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Determine which files are new vs removed
      const currentFileIds = uploadedFiles.map(f => f.id);
      const newFileIds = currentFileIds.filter(id => !initialAttachmentIds.includes(id));
      const removedFileIds = initialAttachmentIds.filter(id => !currentFileIds.includes(id));

      const data = {
        ...certForm,
        issue_date: new Date(certForm.issue_date).toISOString(),
        expiry_date: certForm.expiry_date ? new Date(certForm.expiry_date).toISOString() : null,
        validity_period_months: certForm.validity_period_months ? parseInt(certForm.validity_period_months) : null,
        site_id: activeSiteId || undefined,
        site_name: activeSiteName || undefined,
      };

      if (editingCert) {
        await axios.put(
          `${API}/certifications/${editingCert.id}`,
          data,
          { headers }
        );

        // Attach new files
        if (newFileIds.length > 0) {
          await axios.post(
            `${API}/certifications/${editingCert.id}/attachments`,
            { file_ids: newFileIds },
            { headers }
          );
        }

        // Detach removed files
        for (const fileId of removedFileIds) {
          try {
            await axios.delete(
              `${API}/certifications/${editingCert.id}/attachments/${fileId}`,
              { headers }
            );
          } catch (err) {
            console.error('Failed to detach file:', err);
          }
        }
      } else {
        // Include file_ids on create
        if (newFileIds.length > 0) {
          data.file_ids = newFileIds;
        }
        await axios.post(
          `${API}/certifications`,
          data,
          { headers }
        );
      }

      setShowModal(false);
      fetchData();
      showSuccess(editingCert ? 'Certification updated successfully' : 'Certification added successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save certification');
    }
  };

  const handleEmployeeSelect = (empId) => {
    const employee = employees.find(emp => emp.id === empId);
    if (employee) {
      setCertForm({
        ...certForm,
        employee_id: empId,
        employee_name: employee.name
      });
      setEmployeeSearch(employee.name);
      setShowEmployeeDropdown(false);
    }
  };

  const handleClearEmployee = () => {
    setCertForm({
      ...certForm,
      employee_id: '',
      employee_name: ''
    });
    setEmployeeSearch('');
    setShowEmployeeDropdown(true);
  };

  const handleViewAttachments = async (e, certId) => {
    e.stopPropagation();
    setViewingAttachments([]);
    setShowAttachmentViewer(true);
    setViewingAttachmentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/certifications/${certId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewingAttachments(res.data.attachments || []);
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
      setViewingAttachments([]);
    } finally {
      setViewingAttachmentLoading(false);
    }
  };

  const handleDownloadViewedAttachment = async (fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const link = document.createElement('a');
      link.href = res.data.download_url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      showError('Failed to download attachment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Valid': return 'bg-green-100 text-green-700';
      case 'Expiring Soon': return 'bg-yellow-100 text-yellow-700';
      case 'Expired': return 'bg-red-100 text-red-700';
      case 'Pending Renewal': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDaysToExpiry = (expiryDate) => {
    if (!expiryDate) return null;

    // Parse expiry date and get today's date at midnight (start of day)
    const expiry = new Date(expiryDate);
    const today = new Date();

    // Reset both dates to midnight (00:00:00) to compare only dates, not times
    expiry.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const days = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/training')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Training Dashboard
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">🎓 Certifications & Licenses</h1>
              <p className="text-gray-600 mt-1">{filtered.length} certification(s) found</p>
            </div>
            {hasPermission('training', 'create') && (
              <button
                onClick={() => openModal()}
                className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Certification
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex gap-2 flex-wrap">
            {['All', 'Valid', 'Expiring Soon', 'Expired', 'Pending Renewal'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  filterStatus === status
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Certifications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(cert => {
            const daysToExpiry = getDaysToExpiry(cert.expiry_date);
            return (
              <div key={cert.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/training/certifications/${cert.id}`)}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex gap-2 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(cert.status)}`}>
                      {cert.status}
                    </span>
                    {cert.linked_training_id && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                        📚 From Training
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="font-bold text-lg text-gray-800 mb-2">{cert.certificate_name}</h3>
                <p className="text-sm text-gray-600 mb-1">{cert.employee_name}</p>
                <p className="text-xs text-gray-500 mb-3">{cert.certificate_type}</p>

                <div className="space-y-1 text-sm border-t pt-3">
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 shrink-0">Issuing Body:</span>
                    <span className="font-semibold text-gray-800 truncate text-right" title={cert.issuing_body}>{cert.issuing_body}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 shrink-0">Certificate #:</span>
                    <span className="font-mono text-xs text-gray-800 truncate text-right" title={cert.certificate_number}>{cert.certificate_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Issue Date:</span>
                    <span className="text-gray-800">{formatDate(cert.issue_date)}</span>
                  </div>
                  {cert.expiry_date && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expiry Date:</span>
                        <span className={`font-semibold ${
                          cert.status === 'Expired' ? 'text-red-600' :
                          cert.status === 'Expiring Soon' ? 'text-yellow-600' :
                          'text-gray-800'
                        }`}>
                          {formatDate(cert.expiry_date)}
                        </span>
                      </div>
                      {daysToExpiry !== null && daysToExpiry >= 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Days Left:</span>
                          <span className={`font-bold ${
                            daysToExpiry <= 30 ? 'text-red-600' :
                            daysToExpiry <= 60 ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {daysToExpiry} days
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  {cert.auto_renewal && (
                    <div className="flex items-center gap-1 text-green-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="text-xs font-semibold">Auto-renewal</span>
                    </div>
                  )}
                  <button
                    onClick={(e) => handleViewAttachments(e, cert.id)}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs mt-1"
                    title="View attachments"
                  >
                    📎 Attachments
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No certifications found</p>
          </div>
        )}

        {/* Certification Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingCert ? 'Edit Certification' : 'Add Certification'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Employee - Searchable Dropdown */}
                  <div className="col-span-2 relative" ref={dropdownRef}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Employee <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        onFocus={() => !editingCert && setShowEmployeeDropdown(true)}
                        disabled={!!editingCert}
                        placeholder="Search by name, email, or ID..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                      />
                      {certForm.employee_id && !editingCert && (
                        <button
                          type="button"
                          onClick={handleClearEmployee}
                          className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {showEmployeeDropdown && !editingCert && filteredEmployees.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredEmployees.slice(0, 50).map(emp => (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => handleEmployeeSelect(emp.id)}
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 flex justify-between items-center border-b border-gray-100 last:border-b-0"
                          >
                            <div>
                              <div className="font-semibold text-gray-800">{emp.name || 'Unknown'}</div>
                              <div className="text-xs text-gray-500">{emp.email || emp.id || 'N/A'}</div>
                            </div>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{emp.role || 'User'}</span>
                          </button>
                        ))}
                        {filteredEmployees.length > 50 && (
                          <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50">
                            Showing first 50 results. Type to refine search.
                          </div>
                        )}
                      </div>
                    )}
                    {certForm.employee_id && (
                      <p className="text-xs text-green-600 mt-1">✓ Selected: {certForm.employee_name}</p>
                    )}
                  </div>

                  {/* Certificate Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Certificate Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={certForm.certificate_type}
                      onChange={(e) => setCertForm({ ...certForm, certificate_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Safety Certificate">Safety Certificate</option>
                      <option value="Professional License">Professional License</option>
                      <option value="Equipment License">Equipment License</option>
                    </select>
                  </div>

                  {/* Certificate Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Certificate Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={certForm.certificate_name}
                      onChange={(e) => setCertForm({ ...certForm, certificate_name: e.target.value })}
                      placeholder="e.g., Forklift Operator License"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Issuing Body */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Issuing Body <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={certForm.issuing_body}
                      onChange={(e) => setCertForm({ ...certForm, issuing_body: e.target.value })}
                      placeholder="e.g., MOM Singapore, Red Cross"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Certificate Number */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Certificate Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={certForm.certificate_number}
                      onChange={(e) => setCertForm({ ...certForm, certificate_number: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Issue Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Issue Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={certForm.issue_date}
                      onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })}
                      min="2000-01-01"
                      max={todayLocalISO()}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date</label>
                    <input
                      type="date"
                      value={certForm.expiry_date}
                      onChange={(e) => setCertForm({ ...certForm, expiry_date: e.target.value })}
                      min={certForm.issue_date || "2000-01-01"}
                      max="2099-12-31"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Validity Period */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Validity (months)</label>
                    <input
                      type="number"
                      value={certForm.validity_period_months}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d+$/.test(val)) {
                          setCertForm({ ...certForm, validity_period_months: val });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === '.' || e.key === 'e' || e.key === '-' || e.key === '+') e.preventDefault(); }}
                      step="1"
                      min="1"
                      placeholder="e.g., 12, 24, 36"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Auto Renewal */}
                  <div className="col-span-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={certForm.auto_renewal}
                        onChange={(e) => setCertForm({ ...certForm, auto_renewal: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Auto-renewal enabled</span>
                    </label>
                  </div>

                  {/* Certificate File Upload */}
                  <div className="col-span-2">
                    <FileUploadWidget
                      module="training"
                      referenceId={editingCert?.id}
                      maxFiles={5}
                      maxSizeMB={10}
                      allowedExtensions={['pdf', 'png', 'jpg', 'jpeg']}
                      existingFiles={uploadedFiles}
                      onFilesChange={setUploadedFiles}
                    />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!certForm.employee_id || !certForm.certificate_name || !certForm.issuing_body || !certForm.certificate_number}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400"
                >
                  {editingCert ? 'Update Certification' : 'Add Certification'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attachment Viewer Modal */}
        {showAttachmentViewer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-800">📎 Attachments</h2>
                <button
                  onClick={() => setShowAttachmentViewer(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
              <div className="px-6 py-4">
                {viewingAttachmentLoading ? (
                  <div className="flex justify-center items-center h-24">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : viewingAttachments.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">No attachments found for this certification.</p>
                ) : (
                  <div className="space-y-3">
                    {viewingAttachments.map((file) => (
                      <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-lg flex-shrink-0">📄</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{file.filename}</p>
                          {file.file_size && (
                            <p className="text-xs text-gray-500">
                              {file.file_size < 1024 * 1024
                                ? `${(file.file_size / 1024).toFixed(1)} KB`
                                : `${(file.file_size / (1024 * 1024)).toFixed(1)} MB`}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDownloadViewedAttachment(file.id, file.filename)}
                          className="flex-shrink-0 text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowAttachmentViewer(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default Certifications;
